import React, {useEffect, useRef, useState} from 'react'
import {Box, Flex, IconButton, Image, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useMultiStyleConfig} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Navigation} from 'swiper/modules'
import ExpandedImage from './expanded-image'
import {FormattedMessage} from 'react-intl'
import {ChevronLeftIcon, ChevronRightIcon} from '../../icons/index'
import {useReviewPhotos} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'

const SubmittedImages = ({reviews = [], closeAllReviewsModal}) => {
    const [selectedReview, setSelectedReview] = useState({})
    const [isMounted, setIsMounted] = useState(false)
    const styles = useMultiStyleConfig('WriteReview')
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const showDesktop = isMounted && isDesktop
    const BATCH_SIZE = showDesktop ? 10 : 5
    const [visibleCount, setVisibleCount] = useState(5)
    const swiperRef = useRef(null)
    const prevRef = useRef(null)
    const nextRef = useRef(null)

    useEffect(() => {
        setIsMounted(true)
    }, [])

    // All reviews photos
    const allPhotos = useReviewPhotos(reviews)

    const visiblePhotos = allPhotos.slice(0, visibleCount)
    const hasMore = visibleCount < allPhotos.length

    useEffect(() => {
        setVisibleCount((prev) => (prev < BATCH_SIZE ? BATCH_SIZE : prev))
    }, [BATCH_SIZE])

    const handleReachEnd = () => {
        if (hasMore) {
            setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, allPhotos.length))
        }
    }

    // Re-bind swiper navigation once desktop arrows are rendered.
    useEffect(() => {
        if (!swiperRef.current || swiperRef.current.destroyed) {
            return
        }

        const swiper = swiperRef.current

        if (prevRef.current && nextRef.current) {
            swiper.params.navigation.prevEl = prevRef.current
            swiper.params.navigation.nextEl = nextRef.current
            swiper.navigation?.destroy?.()
            swiper.navigation?.init?.()
            swiper.navigation?.update?.()
        }
    }, [visiblePhotos.length])

    // After new slides are added to the DOM, tell Swiper to recalculate
    useEffect(() => {
        if (swiperRef.current && !swiperRef.current.destroyed) {
            swiperRef.current.update()
        }
    }, [visibleCount])

    if (allPhotos.length === 0) {
        return null
    }

    return (
        <Box mt={{base: 9, lg: 8}}>
            <Text as="h3" textStyle="webH3" mb={{base: 3, lg: 4}}>
                <FormattedMessage
                    defaultMessage="Submitted Images"
                    id="pdp_reviews.submitted.images"
                />
            </Text>
            <Box position="relative" h={{base: '80px', lg: '100px'}}>
                <Swiper
                    modules={[Navigation]}
                    slidesPerView={4.5}
                    spaceBetween={1}
                    grabCursor={true}
                    style={{height: '100%'}}
                    navigation={{
                        prevEl: prevRef.current,
                        nextEl: nextRef.current
                    }}
                    onBeforeInit={(swiper) => {
                        swiper.params.navigation.prevEl = prevRef.current
                        swiper.params.navigation.nextEl = nextRef.current
                    }}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper
                    }}
                    onReachEnd={handleReachEnd}
                    breakpoints={{
                        992: {
                            slidesPerView: 9
                        }
                    }}>
                    {visiblePhotos.map((photo) => (
                        <SwiperSlide key={photo.key}>
                            <button
                                type="button"
                                data-testid="submitted-images-slide-button"
                                onClick={(event) =>
                                    setSelectedReview({
                                        review: photo.review,
                                        index: photo.index,
                                        triggerElement: event.currentTarget
                                    })
                                }
                                onKeyDown={(e) =>
                                    e.key === 'Enter' &&
                                    setSelectedReview({
                                        review: photo.review,
                                        index: photo.index,
                                        triggerElement: e.currentTarget
                                    })
                                }
                                onFocus={(event) => {
                                    event.currentTarget.style.outline = '2px solid var(--chakra-colors-webTheme-focus)'
                                    event.currentTarget.style.boxShadow = 'none'
                                }}
                                onBlur={(event) => {
                                    event.currentTarget.style.outline = 'none'
                                    event.currentTarget.style.boxShadow = 'none'
                                }}
                                style={{
                                    cursor: 'pointer',
                                    display: 'block',
                                    width: '100%',
                                    height: '100%',
                                    padding: '2px',
                                    outline: 'none',
                                    border: 0,
                                    background: 'transparent'
                                }}
                            >
                                <Image
                                    src={photo.url}
                                    alt="Submitted review image"
                                    objectFit="cover"
                                    width="100%"
                                    height="100%"
                                />
                            </button>
                        </SwiperSlide>
                    ))}
                </Swiper>

                {allPhotos.length > 1 && (
                    <Flex __css={styles.navContainer}>
                        <IconButton
                            ref={prevRef}
                            aria-label="Previous"
                            variant={'link'}
                            __css={styles.navButton}
                            left={0}
                            className={'prev-photo'}
                            sx={{
                                '&:disabled': {
                                    opacity: '50%',
                                    background: 'white !important'
                                }
                            }}
                            icon={<ChevronLeftIcon />}
                        />
                        <IconButton
                            ref={nextRef}
                            aria-label="Next"
                            variant={'link'}
                            __css={styles.navButton}
                            right={0}
                            className={'next-photo'}
                            sx={{
                                '&:disabled': {
                                    opacity: '50%',
                                    background: 'white !important'
                                }
                            }}
                            icon={<ChevronRightIcon />}
                        />
                    </Flex>
                )}
            </Box>

            {/* Submitted photos modal */}
            <ExpandedImage
                selectedReview={selectedReview}
                setSelectedReview={setSelectedReview}
                closeOtherModals={{closeAllReviewsModal}}
                allReviews={reviews}
            />
        </Box>
    )
}

SubmittedImages.propTypes = {
    reviews: PropTypes.array,
    closeAllReviewsModal: PropTypes.func
}

export default SubmittedImages
